import axios from "axios";

const BASE = import.meta.env.VITE_API_BASE || "/api";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authToken() {
  return localStorage.getItem("token") || "";
}

export const api = {
  getAuthConfig: () =>
    axios.get(`${BASE}/auth/config`).then(r => r.data),

  getSyncStatus: () =>
    axios.get(`${BASE}/auth/sync-status`).then(r => r.data),

  postClientLogs: (entries, appVersion) =>
    axios.post(`${BASE}/client-logs`, { entries, appVersion }, { headers: authHeaders() }).then(r => r.data),

  restartScannerAgent: (reason) =>
    axios.post(`${BASE}/scanner-agent/restart`, { reason }, { headers: authHeaders(), timeout: 30000 }).then(r => r.data),

  listClientLogs: (params = {}) =>
    axios.get(`${BASE}/client-logs`, { params, headers: authHeaders() }).then(r => r.data),

  getClientLogSummary: () =>
    axios.get(`${BASE}/client-logs/summary`, { headers: authHeaders() }).then(r => r.data),
  getClientLogTrail: (id) =>
    axios.get(`${BASE}/client-logs/trail`, { params: { id }, headers: authHeaders() }).then(r => r.data),

  listSyncConflicts: () =>
    axios.get(`${BASE}/sync-conflicts`, { headers: authHeaders() }).then(r => r.data),

  resolveEditConflict: (patientId, choices) =>
    axios.post(`${BASE}/sync-conflicts/edit/${patientId}`, { choices }, { headers: authHeaders() }).then(r => r.data),

  mergeDuplicatePatients: (serverId, survivorId) =>
    axios.post(`${BASE}/sync-conflicts/merge`, { serverId, survivorId }, { headers: authHeaders() }).then(r => r.data),

  listDocuments: ({ limit = 60, offset = 0, q, location, dateFrom, dateTo } = {}) =>
    axios.get(`${BASE}/documents`, { params: { limit, offset, q, location, dateFrom, dateTo }, headers: authHeaders() }).then(r => r.data),

  getDocumentPurchaseState: (id) =>
    axios.get(`${BASE}/documents/${id}/purchase-state`, { headers: authHeaders() }).then(r => r.data),

  getDocument: (id) =>
    axios.get(`${BASE}/documents/${id}`, { headers: authHeaders() }).then(r => r.data),

  saveDocument: ({ operationId, name, pdfBase64, rawBase64, scanState, annotations, pageCount, patientId, footSide, footSizeMm, diseases, footData, generalSymptoms, complaintsFormat, reportHtml, insoleType, noteComment, noteRecommendations, internalNote, exercises, yclientsRecordId, yclientsRecordLabelIds, messenger, messengerLink }) =>
    axios.post(`${BASE}/documents`, { operationId, name, pdfBase64, rawBase64, scanState, annotations, pageCount, patientId, footSide, footSizeMm, diseases, footData, generalSymptoms, complaintsFormat, reportHtml, insoleType, noteComment, noteRecommendations, internalNote, exercises, yclientsRecordId, yclientsRecordLabelIds, messenger, messengerLink }, { headers: authHeaders() }).then(r => r.data),

  updateDocument: (id, { annotations, footData, scanState, reportHtml, noteComment, noteRecommendations, internalNote, footSizeMm, diseases, exercises, generalSymptoms, yclientsRecordLabelIds, messenger, messengerLink, patientAlias }) =>
    axios.patch(`${BASE}/documents/${id}`, { annotations, footData, scanState, reportHtml, noteComment, noteRecommendations, internalNote, footSizeMm, diseases, exercises, generalSymptoms, yclientsRecordLabelIds, messenger, messengerLink, patientAlias }, { headers: authHeaders() }).then(r => r.data),

  reassignDocumentPatient: (id, patientId) =>
    axios.patch(`${BASE}/documents/${id}/reassign-patient`, { patientId }, { headers: authHeaders() }).then(r => r.data),

  updateAnnotations: (id, annotations) =>
    axios.patch(`${BASE}/documents/${id}/annotations`, { annotations }, { headers: authHeaders() }).then(r => r.data),

  deleteDocument: (id) =>
    axios.delete(`${BASE}/documents/${id}`, { headers: authHeaders() }).then(r => r.data),

  getPublicLink: (id) =>
    axios.post(`${BASE}/documents/${id}/public-link`, {}, { headers: authHeaders() }).then(r => r.data),
  regeneratePublicLink: (id) =>
    axios.delete(`${BASE}/documents/${id}/public-link`, { headers: authHeaders() }).then(r => r.data),

  fileUrl: (id) => `${BASE}/documents/${id}/file?token=${encodeURIComponent(authToken())}`,
  rawFileUrl: (id) => `${BASE}/documents/${id}/raw-file?token=${encodeURIComponent(authToken())}`,

  uploadDocumentFiles: (docId, files) =>
    axios.post(`${BASE}/documents/${docId}/files`, { files }, { headers: authHeaders() }).then(r => r.data),

  listDocumentFiles: (docId) =>
    axios.get(`${BASE}/documents/${docId}/files`, { headers: authHeaders() }).then(r => r.data),

  deleteDocumentFile: (docId, fileId) =>
    axios.delete(`${BASE}/documents/${docId}/files/${fileId}`, { headers: authHeaders() }).then(r => r.data),

  documentFileUrl: (docId, fileId) =>
    `${BASE}/documents/${docId}/files/${fileId}?token=${encodeURIComponent(authToken())}`,

  // Patients
  listPatients: ({ limit = 60, offset = 0, q, location } = {}) =>
    axios.get(`${BASE}/patients`, { params: { limit, offset, q, location }, headers: authHeaders() }).then(r => r.data),

  getPatient: (id) =>
    axios.get(`${BASE}/patients/${id}`, { headers: authHeaders() }).then(r => r.data),

  createPatient: ({ name, birthDate, notes, phone, crmLink, socialNetwork, internalNote }) =>
    axios.post(`${BASE}/patients`, { name, birthDate, notes, phone, crmLink, socialNetwork, internalNote }, { headers: authHeaders() }).then(r => r.data),

  updatePatient: (id, data) =>
    axios.patch(`${BASE}/patients/${id}`, data, { headers: authHeaders() }).then(r => r.data),

  deletePatient: (id) =>
    axios.delete(`${BASE}/patients/${id}`, { headers: authHeaders() }).then(r => r.data),

  addPurchase: (patientId, { name, price, date, productId, documentId, yclientsRecordId, operationId }) =>
    axios.post(`${BASE}/patients/${patientId}/purchases`, { name, price, date, productId, documentId, yclientsRecordId, operationId }, { headers: authHeaders() }).then(r => r.data),

  // Phase 2 — реальная оплата визита (услуга + списанные товары) в YClients, дёргается после того
  // как все покупки со скана уже созданы. Fire-and-forget на бэке, тут просто триггер.
  pushDocumentPayment: (id) =>
    axios.post(`${BASE}/documents/${id}/yclients-pay`, {}, { headers: authHeaders() }).then(r => r.data),

  // Тот же смысл, но для покупок, добавленных прямо из карточки пациента с ручным выбором записи
  // (нет scanapp-документа, которому можно было бы дёрнуть pushDocumentPayment).
  payPurchaseRecord: (patientId, yclientsRecordId) =>
    axios.post(`${BASE}/patients/${patientId}/yclients-pay-record`, { yclientsRecordId }, { headers: authHeaders() }).then(r => r.data),

  matchYclientsPatient: ({ yclientsClientId, name, phone }) =>
    axios.post(`${BASE}/patients/match-yclients`, { yclientsClientId, name, phone }, { headers: authHeaders() }).then(r => r.data),

  // «Разобрать неразобранное» (центр)
  listUnmatchedPatients: ({ limit = 60, offset = 0 } = {}) =>
    axios.get(`${BASE}/patients/unmatched`, { params: { limit, offset }, headers: authHeaders() }).then(r => r.data),
  linkYclientsPatient: (patientId, yclientsClientId, companyId) =>
    axios.post(`${BASE}/patients/${patientId}/link-yclients`, { yclientsClientId, companyId }, { headers: authHeaders() }).then(r => r.data),
  mergePatients: (survivorId, { otherId, keepDocumentIds, keepPurchaseIds, purchaseDocuments, fields }) =>
    axios.post(`${BASE}/patients/${survivorId}/merge`, { otherId, keepDocumentIds, keepPurchaseIds, purchaseDocuments, fields }, { headers: authHeaders() }).then(r => r.data),
  skipYclientsPatient: (patientId) =>
    axios.post(`${BASE}/patients/${patientId}/skip-yclients`, {}, { headers: authHeaders() }).then(r => r.data),
  searchYclientsClientAdmin: (phone) =>
    axios.post(`${BASE}/yclients/admin/clients/search`, { phone }, { headers: authHeaders() }).then(r => r.data),
  getYclientsClientRecordsAdmin: (clientId, companyId) =>
    axios.get(`${BASE}/yclients/admin/clients/${clientId}/records`, { params: { companyId }, headers: authHeaders() }).then(r => r.data),
  setDocumentYclientsRecord: (docId, yclientsRecordId) =>
    axios.patch(`${BASE}/documents/${docId}/yclients-record`, { yclientsRecordId }, { headers: authHeaders() }).then(r => r.data),
  setDocumentYclientsSkipped: (docId, skipped) =>
    axios.patch(`${BASE}/documents/${docId}/yclients-record`, { skipped }, { headers: authHeaders() }).then(r => r.data),
  getYclientsClientAdmin: (clientId, companyId) =>
    axios.get(`${BASE}/yclients/admin/clients/${clientId}`, { params: { companyId }, headers: authHeaders() }).then(r => r.data),

  deletePurchase: (patientId, purchaseId) =>
    axios.delete(`${BASE}/patients/${patientId}/purchases/${purchaseId}`, { headers: authHeaders() }).then(r => r.data),

  // Users (admin only)
  listUsers: () =>
    axios.get(`${BASE}/users`, { headers: authHeaders() }).then(r => r.data),

  createUser: ({ username, password, role, fullName, yclientsStaffId }) =>
    axios.post(`${BASE}/users`, { username, password, role, fullName, yclientsStaffId }, { headers: authHeaders() }).then(r => r.data),

  updateUser: (id, { fullName, yclientsStaffId }) =>
    axios.patch(`${BASE}/users/${id}`, { fullName, yclientsStaffId }, { headers: authHeaders() }).then(r => r.data),

  // Сотрудники всех филиалов YClients — для выбора ФИО пользователя (только central-admin).
  getYclientsAllStaff: () =>
    axios.get(`${BASE}/yclients/admin/staff`, { headers: authHeaders() }).then(r => r.data),

  deleteUser: (id) =>
    axios.delete(`${BASE}/users/${id}`, { headers: authHeaders() }).then(r => r.data),

  changePassword: (id, password) =>
    axios.patch(`${BASE}/users/${id}/password`, { password }, { headers: authHeaders() }).then(r => r.data),

  syncStatus: () =>
    axios.get(`${BASE}/sync/status`, { headers: authHeaders() }).then(r => r.data),

  sync: () =>
    axios.post(`${BASE}/sync`, {}, { headers: authHeaders() }).then(r => r.data),

  recheckOnline: () =>
    axios.post(`${BASE}/sync/recheck`, {}, { headers: authHeaders() }).then(r => r.data),

  resetCatalog: () =>
    axios.post(`${BASE}/sync/reset-catalog`, {}, { headers: authHeaders() }).then(r => r.data),

  getSyncLog: ({ location, status, limit, offset } = {}) =>
    axios.get(`${BASE}/sync/log`, { params: { location, status, limit, offset }, headers: authHeaders() }).then(r => r.data),

  getSyncIssues: () =>
    axios.get(`${BASE}/sync/issues`, { headers: authHeaders() }).then(r => r.data),
  reconcileSyncIssue: (id) =>
    axios.post(`${BASE}/sync/issues/${id}/reconcile`, {}, { headers: authHeaders() }).then(r => r.data),
  listSyncKeys: () =>
    axios.get(`${BASE}/sync/keys`, { headers: authHeaders() }).then(r => r.data),
  createSyncKey: (location) =>
    axios.post(`${BASE}/sync/keys`, { location }, { headers: authHeaders() }).then(r => r.data),
  revokeSyncKey: (id) =>
    axios.delete(`${BASE}/sync/keys/${id}`, { headers: authHeaders() }).then(r => r.data),

  // Catalog
  listDiseaseTypes: () =>
    axios.get(`${BASE}/catalog/diseases`, { headers: authHeaders() }).then(r => r.data),
  createDiseaseType: (name, location) =>
    axios.post(`${BASE}/catalog/diseases`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteDiseaseType: (id) =>
    axios.delete(`${BASE}/catalog/diseases/${id}`, { headers: authHeaders() }).then(r => r.data),

  listComplaintTypes: () =>
    axios.get(`${BASE}/catalog/complaint-types`, { headers: authHeaders() }).then(r => r.data),
  createComplaintType: (name, location) =>
    axios.post(`${BASE}/catalog/complaint-types`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteComplaintType: (id) =>
    axios.delete(`${BASE}/catalog/complaint-types/${id}`, { headers: authHeaders() }).then(r => r.data),

  listFootTypes: () =>
    axios.get(`${BASE}/catalog/foot-types`, { headers: authHeaders() }).then(r => r.data),
  createFootType: (name, location) =>
    axios.post(`${BASE}/catalog/foot-types`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteFootType: (id) =>
    axios.delete(`${BASE}/catalog/foot-types/${id}`, { headers: authHeaders() }).then(r => r.data),

  listCornZones: () =>
    axios.get(`${BASE}/catalog/corn-zones`, { headers: authHeaders() }).then(r => r.data),
  createCornZone: (name, location) =>
    axios.post(`${BASE}/catalog/corn-zones`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteCornZone: (id) =>
    axios.delete(`${BASE}/catalog/corn-zones/${id}`, { headers: authHeaders() }).then(r => r.data),

  listGeneralSymptoms: () =>
    axios.get(`${BASE}/catalog/general-symptoms`, { headers: authHeaders() }).then(r => r.data),
  createGeneralSymptom: (name, location) =>
    axios.post(`${BASE}/catalog/general-symptoms`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteGeneralSymptom: (id) =>
    axios.delete(`${BASE}/catalog/general-symptoms/${id}`, { headers: authHeaders() }).then(r => r.data),

  listShoeTypes: () =>
    axios.get(`${BASE}/catalog/shoe-types`, { headers: authHeaders() }).then(r => r.data),
  createShoeType: (name, location) =>
    axios.post(`${BASE}/catalog/shoe-types`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteShoeType: (id) =>
    axios.delete(`${BASE}/catalog/shoe-types/${id}`, { headers: authHeaders() }).then(r => r.data),

  listPadSizes: () =>
    axios.get(`${BASE}/catalog/pad-sizes`, { headers: authHeaders() }).then(r => r.data),
  createPadSize: (name, length_mm, width_mm, location) =>
    axios.post(`${BASE}/catalog/pad-sizes`, { name, length_mm, width_mm, location }, { headers: authHeaders() }).then(r => r.data),
  deletePadSize: (id) =>
    axios.delete(`${BASE}/catalog/pad-sizes/${id}`, { headers: authHeaders() }).then(r => r.data),

  listLoadTypes: () =>
    axios.get(`${BASE}/catalog/load-types`, { headers: authHeaders() }).then(r => r.data),
  createLoadType: (name, location) =>
    axios.post(`${BASE}/catalog/load-types`, { name, location }, { headers: authHeaders() }).then(r => r.data),
  deleteLoadType: (id) =>
    axios.delete(`${BASE}/catalog/load-types/${id}`, { headers: authHeaders() }).then(r => r.data),

  listLocations: () =>
    axios.get(`${BASE}/catalog/locations`).then(r => r.data),
  createLocation: (name) =>
    axios.post(`${BASE}/catalog/locations`, { name }, { headers: authHeaders() }).then(r => r.data),
  deleteLocation: (id) =>
    axios.delete(`${BASE}/catalog/locations/${id}`, { headers: authHeaders() }).then(r => r.data),

  listProducts: () =>
    axios.get(`${BASE}/catalog/products`, { headers: authHeaders() }).then(r => r.data),
  createProduct: ({ name, price, category, article, groupId, photoBase64, location }) =>
    axios.post(`${BASE}/catalog/products`, { name, price, category, article, groupId, photoBase64, location }, { headers: authHeaders() }).then(r => r.data),
  updateProduct: (id, { groupId }) =>
    axios.patch(`${BASE}/catalog/products/${id}`, { groupId }, { headers: authHeaders() }).then(r => r.data),
  deleteProduct: (id) =>
    axios.delete(`${BASE}/catalog/products/${id}`, { headers: authHeaders() }).then(r => r.data),
  productPhotoUrl: (id) =>
    `${BASE}/catalog/products/${id}/photo?token=${encodeURIComponent(authToken())}`,

  listProductGroups: (category) =>
    axios.get(`${BASE}/catalog/product-groups`, { params: { category }, headers: authHeaders() }).then(r => r.data),
  createProductGroup: ({ name, category, parentId, location }) =>
    axios.post(`${BASE}/catalog/product-groups`, { name, category, parentId, location }, { headers: authHeaders() }).then(r => r.data),
  renameProductGroup: (id, name) =>
    axios.patch(`${BASE}/catalog/product-groups/${id}`, { name }, { headers: authHeaders() }).then(r => r.data),
  deleteProductGroup: (id) =>
    axios.delete(`${BASE}/catalog/product-groups/${id}`, { headers: authHeaders() }).then(r => r.data),

  listExercises: () =>
    axios.get(`${BASE}/catalog/exercises`, { headers: authHeaders() }).then(r => r.data),
  createExercise: ({ name, description, link, photoBase64, location }) =>
    axios.post(`${BASE}/catalog/exercises`, { name, description, link, photoBase64, location }, { headers: authHeaders() }).then(r => r.data),
  deleteExercise: (id) =>
    axios.delete(`${BASE}/catalog/exercises/${id}`, { headers: authHeaders() }).then(r => r.data),
  exercisePhotoUrl: (id) =>
    `${BASE}/catalog/exercises/${id}/photo?token=${encodeURIComponent(authToken())}`,

  submitFeedback: (payload) =>
    axios.post(`${BASE}/feedback`, payload, { headers: authHeaders() }).then(r => r.data),

  // YClients (admin only, тестовый импорт товаров)
  getYclientsCompanies: () =>
    axios.get(`${BASE}/yclients/companies`, { headers: authHeaders() }).then(r => r.data),
  syncYclientsGoods: (companyId) =>
    axios.post(`${BASE}/yclients/goods/sync`, { companyId }, { headers: authHeaders() }).then(r => r.data),
  getYclientsCatalog: () =>
    axios.get(`${BASE}/yclients/catalog`, { headers: authHeaders() }).then(r => r.data),
  setYclientsLocationCompany: (locationId, companyId) =>
    axios.patch(`${BASE}/yclients/locations/${locationId}`, { companyId }, { headers: authHeaders() }).then(r => r.data),
  syncYclientsLocation: (locationId) =>
    axios.post(`${BASE}/yclients/goods/sync-location/${locationId}`, {}, { headers: authHeaders() }).then(r => r.data),
  syncYclientsAllLocations: () =>
    axios.post(`${BASE}/yclients/goods/sync-all`, {}, { headers: authHeaders() }).then(r => r.data),
  clearYclientsProducts: (scope) =>
    axios.post(`${BASE}/yclients/products/clear`, { scope }, { headers: authHeaders() }).then(r => r.data),
  getYclientsCommonBinding: () =>
    axios.get(`${BASE}/yclients/common-binding`, { headers: authHeaders() }).then(r => r.data),
  setYclientsCommonBinding: (companyId) =>
    axios.patch(`${BASE}/yclients/common-binding`, { companyId }, { headers: authHeaders() }).then(r => r.data),
  syncYclientsCommon: () =>
    axios.post(`${BASE}/yclients/goods/sync-common`, {}, { headers: authHeaders() }).then(r => r.data),

  // YClients — поиск клиента своей точки по телефону/имени и его записи (для сканера)
  searchYclientsClient: (phone) =>
    axios.get(`${BASE}/yclients/clients/search`, { params: { phone }, headers: authHeaders() }).then(r => r.data),
  getYclientsClientRecords: (clientId) =>
    axios.get(`${BASE}/yclients/clients/${clientId}/records`, { headers: authHeaders() }).then(r => r.data),
  // date (YYYY-MM-DD) необязателен — без него сегодня; с ним — навигация по дням в Журнале.
  getYclientsRecordsToday: (date) =>
    axios.get(`${BASE}/yclients/records/today`, { params: date ? { date } : undefined, headers: authHeaders() }).then(r => r.data),
  getYclientsStaff: () =>
    axios.get(`${BASE}/yclients/staff`, { headers: authHeaders() }).then(r => r.data),
  // "Категория записи" — на самом деле record_labels (entity=2) в YClients, не отдельное доп.поле.
  getYclientsRecordLabels: () =>
    axios.get(`${BASE}/yclients/record-labels`, { headers: authHeaders() }).then(r => r.data),
};

export default api;
